import { PenLine, CheckCircle2, ShieldCheck, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function SignatureCircuit({ pdp, operationId, onSigned }) {
  const { user } = useAuth();
  if (!pdp || pdp.statutSignature === "non_genere") return null;

  const ctrl = pdp.controleAvantDemarrage || { obligatoires: [], bloque: false, manquants: [] };
  const signed = (id) => pdp.signatures.find((s) => s.entiteId === id);
  const sign = async (entiteId) => {
    try {
      await api.post(`/operations/${operationId}/pdp/signer`, { entiteId, nom: user?.name });
      toast.success("Signature enregistrée");
      onSigned && onSigned();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="card" data-testid="signature-circuit">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ fontSize: "1rem" }}>Circuit de signatures</h3>
        <span className={`badge ${pdp.statutSignature === "signe" ? "b-valide" : "b-a_verifier"}`} data-testid="signature-statut">
          {pdp.statutSignature === "signe" ? "Signé" : "À signer"}
        </span>
      </div>
      <div style={{ fontSize: ".74rem", color: "var(--text-muted)", margin: "4px 0 10px" }}>
        Version {pdp.version} · empreinte <span className="mono">{(pdp.hash || "").slice(0, 16)}…</span>
      </div>

      {ctrl.obligatoires.length > 0 && (
        <div className="notice" data-testid="controle-demarrage"
          style={{ marginBottom: 10, background: ctrl.bloque ? "var(--rouge-p, #FBEBE8)" : "var(--vert-p)",
            borderColor: ctrl.bloque ? "var(--rouge, #B3382C)" : "var(--vert)" }}>
          <b style={{ display: "flex", alignItems: "center", gap: 6 }}><Lock size={14} /> Contrôle avant démarrage — annexes obligatoires</b>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: ".82rem", lineHeight: 1.6 }}>
            {ctrl.obligatoires.map((o, i) => (
              <li key={i} data-testid={`ctrl-annexe-${i}`}>{o.present ? "✔" : "✘"} {o.label} — {o.present ? "présente" : "manquante"}</li>
            ))}
          </ul>
          {ctrl.bloque && <div style={{ marginTop: 6, fontSize: ".82rem", fontWeight: 600 }}>
            Signature bloquée : cochez ces annexes « présentes » dans les compléments documentaires du plan.
          </div>}
        </div>
      )}

      {pdp.signatairesRequis.map((s) => {
        const sg = signed(s.entiteId);
        return (
          <div className="ck" key={s.entiteId} data-testid={`sign-${s.entiteId}`} style={{ alignItems: "center" }}>
            {sg ? <CheckCircle2 size={16} color="var(--vert)" /> : <PenLine size={16} color="var(--text-muted)" />}
            <span style={{ flex: 1 }}><b>{s.role}</b> — {s.nom}
              {sg && <span style={{ fontSize: ".74rem", color: "var(--text-muted)", marginLeft: 6 }}>signé le {sg.signeLe} · {sg.hash.slice(0, 10)}…</span>}
            </span>
            {!sg && <button className="btn btn-g btn-sm" data-testid={`sign-btn-${s.entiteId}`}
              disabled={ctrl.bloque} title={ctrl.bloque ? "Annexes obligatoires manquantes" : ""}
              onClick={() => sign(s.entiteId)}>Signer</button>}
          </div>
        );
      })}
      {pdp.statutSignature === "signe"
        ? <div className="notice" style={{ background: "var(--vert-p)", borderColor: "var(--vert)", marginTop: 10 }}><ShieldCheck size={14} style={{ verticalAlign: -2 }} /> Toutes les signatures obligatoires sont réunies. Toute modification du plan créera une nouvelle version et relancera le circuit.</div>
        : <div className="notice" style={{ marginTop: 10 }}>Le document reste « À signer » tant que toutes les signatures obligatoires ne sont pas réalisées.</div>}
    </div>
  );
}
